import { useContext, useMemo } from "react";
import { useTranslation as useI18nextTranslation } from "react-i18next";
import type { TOptions } from "i18next";

import { InitializedTranslationsContext } from "./createTranslationsPageProvider";
import { TranslationHookContext } from "./TranslationHookProvider";
import { useTranslationsNamespace } from "./TranslationsNamespaceProvider";
import {
  type StringMap,
  type UseTranslationHook,
  type UseTranslationOptions,
} from "./types";

export const useInternalTranslation = ({
  namespace,
}: UseTranslationOptions = {}) => {
  const contextNamespace = useTranslationsNamespace();
  return useI18nextTranslation(namespace ?? contextNamespace, {
    useSuspense: false,
  });
};

export const useTranslationFromServerSide: UseTranslationHook = ({
  namespace,
} = {}) => {
  const contextNamespace = useTranslationsNamespace();
  const { t } = useI18nextTranslation(namespace ?? contextNamespace);

  return useMemo(
    () => ({
      t: (key: string, variables?: TOptions<StringMap>) => t(key, variables),
      t0: (key: string, variables?: TOptions<StringMap>) =>
        t(key, {
          ...variables,
          context: variables?.count === 0 ? "none" : "some",
        }),
    }),
    [t],
  );
};

export const useTranslationFromClientSide: UseTranslationHook = (
  options = {},
) => {
  const initialized = useContext(InitializedTranslationsContext);
  const { t, ready } = useInternalTranslation(options);

  return useMemo(
    () => ({
      t: (key: string, variables?: TOptions<StringMap>) =>
        initialized && ready ? t(key, variables) : "",
      t0: (key: string, variables?: TOptions<StringMap>) =>
        initialized && ready
          ? t(key, {
              ...variables,
              context: variables?.count === 0 ? "none" : "some",
            })
          : "",
    }),
    [t, initialized, ready],
  );
};

export const useTranslation: UseTranslationHook = (options = {}) => {
  const useTranslationHookFromContext = useContext(TranslationHookContext);
  const useTranslationInnerHook =
    useTranslationHookFromContext || useTranslationFromClientSide;
  return useTranslationInnerHook(options);
};
